import React, { useEffect } from 'react';

import { groupBy, map } from 'lodash';
import { useSelector } from 'react-redux';

import { RootState } from '../../../types';
import { cn } from '../../../utils/classUtils';
import { scrollToBottom } from '../../../utils/commonUtils';
import { displayDate, isToday } from '../../../utils/DateUtils';
import { SenderReceiverEnum } from '../../../utils/LeoUtils';
import DefaultLoader from '../../DefaultLoader';
import NeoLeoChatBubble from './NeoLeoResponse';
import { NeoLeoResponseLoadingSkeleton } from './NeoLeoResponseLoadingSkeleton';
import NeoLeoUserChatBubble from './NeoLeoUserChatBubble';

interface NeoLeoChatProps {
  isShowingHistory: boolean;
}

const NeoLeoChat: React.FC<NeoLeoChatProps> = ({ isShowingHistory }) => {
  const {
    currentChat,
    isChatLoading,
    msgContext,
    isLeoThinking,
    isLeoGenerating,
    sessionDisclaimer,
    isSessionLoading,
  } = useSelector((state: RootState) => ({
    currentChat: state.leo.currentChat,
    isChatLoading: state.leo.isChatLoading,
    msgContext: state.leo.msgContext,
    isLeoThinking: state.leo.isLeoThinking,
    isLeoGenerating: state.leo.isLeoGenerating,
    sessionDisclaimer: state.leo.session?.disclaimer,
    isSessionLoading: state.leo.isSessionLoading,
  }));
  const currentMessage = currentChat[currentChat.length - 1]
    ? currentChat[currentChat.length - 1].message
    : '';

  useEffect(() => {
    if (!isChatLoading) {
      scrollToBottom(document?.getElementById('leo-chat-box')!);
    }
  }, [currentMessage, msgContext, isChatLoading]);

  const chatGroupedByDate = groupBy(currentChat, (el) =>
    displayDate(el.created_at!!),
  );

  const getGroupedByDateHeader = (createdAt: string) => {
    const dateString = displayDate(createdAt);
    if (isToday(createdAt)) {
      return `Today ${dateString}`;
    }
    return dateString;
  };

  return (
    <div
      className='h-full flex-grow px-4 pb-4 overflow-y-auto scrollbar-hide'
      id='leo-chat-box'
    >
      {isChatLoading || isSessionLoading ? (
        <DefaultLoader />
      ) : (
        map(chatGroupedByDate, (chatByDate) => (
          <div key={chatByDate[0].created_at!}>
            <div className='w-full flex justify-center px-2'>
              <div
                className={cn(
                  'rounded-lg border border-reskin-regent-300 border-opacity-30 px-3 py-2',
                )}
              >
                <p
                  className='text-xs font-normal text-reskin-regent-300 font-inter text-center text-opacity-75'
                  data-testid='session-disclaimer'
                >
                  {sessionDisclaimer}
                </p>
              </div>
            </div>

            <div>
              <div className='line-with-words-center px-8 my-3 after:border-b before:border-b after:border-b-reskin-grey-400 before:border-b-reskin-grey-400'>
                <p className='text-leo-response-action text-xs px-2.5'>
                  {getGroupedByDateHeader(chatByDate[0].created_at!)}
                </p>
              </div>
              {(chatByDate || []).map((msgRes, ind) =>
                msgRes.sender === SenderReceiverEnum.SYSTEM ? (
                  <NeoLeoChatBubble
                    key={msgRes.id}
                    {...msgRes}
                    isShowingHistory={isShowingHistory}
                    isDisclaimer={ind === 0}
                  />
                ) : (
                  <NeoLeoUserChatBubble
                    key={msgRes.id}
                    text={msgRes.message!}
                  />
                ),
              )}
              {isLeoThinking && !isLeoGenerating && (
                <NeoLeoResponseLoadingSkeleton className='ml-2' />
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default NeoLeoChat;
